import View from '@play-co/timestep-core/lib/ui/View';
import ImageScaleView from '@play-co/timestep-core/lib/ui/ImageScaleView';
import QuickViewPool from '@play-co/timestep-core/lib/ui/ViewPool2';
import { gsap } from 'gsap';
import { RadialShine } from 'src/game/components/shared/RadialShine';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { getRootView } from 'src/lib/utils';
import StateObserver from 'src/StateObserver';
import sounds from 'src/lib/sounds';

/** Base burst lifetime in seconds; larger values slow its movement and fade. */
const BURST_PARTICLE_LIFETIME = 2;
/** Burst spawn distance from the visible number's center, in game UI units. */
const BURST_PARTICLE_SPAWN_RADIUS = 30;
/** Base outward travel distance from each burst particle's spawn point, in game UI units. */
const BURST_PARTICLE_BASE_DISTANCE = 100;
/** Maximum random extra travel distance added to the burst base, in game UI units. */
const BURST_PARTICLE_RANDOM_DISTANCE = 100;
/** Lifetime in seconds of each stationary trail sparkle. */
const TRAIL_PARTICLE_LIFETIME = 2;
/** Minimum spawn distance from the visible number's center, in game UI units. */
const TRAIL_PARTICLE_MIN_RADIUS = 35;
/** Maximum spawn distance from the visible number's center, in game UI units. */
const TRAIL_PARTICLE_MAX_RADIUS = 55;

/** Font size of the bonus moves revealed above the popup. */
const EXTRA_MOVES_FONT_SIZE = 100;
/** Spawn Y as a fraction of the screen height, capped by the minimum badge gap. */
const EXTRA_MOVES_SPAWN_Y_RATIO = 0.25;
/** Minimum vertical gap above the badge at spawn, in game UI units. */
const EXTRA_MOVES_MIN_BADGE_GAP = 300;
/** Duration in seconds of the bonus number's scale-up reveal. */
const EXTRA_MOVES_SCALE_UP_DURATION = 0.3;
/** Duration in seconds of the bonus number's flight into the badge. */
const EXTRA_MOVES_TRAVEL_DURATION = 0.55;

/** Lazily created pool retained across offers to reuse sparkle views. */
let sparklePool: QuickViewPool<ImageScaleView> | undefined;

/**
 * Reveal an increased gem offer above the game, then restore the popup's icon and label.
 * @param args Existing badge and label, regular and bonus moves, and count updater.
 * @returns Resolves after completion, with interaction restored.
 */
export async function playExtraMovesOfferAnimation({
  badge,
  label,
  regularMoves,
  setNumMoves,
  bonusMoves,
}: {
  badge: View;
  label: LangBitmapFontTextView;
  regularMoves: number;
  setNumMoves: (moves: number) => void;
  bonusMoves: number;
}): Promise<void> {
  // Regular offers need no presentation.
  if (bonusMoves <= 0) return;

  // Derive the final count from the supplied offer.
  const totalMoves = regularMoves + bonusMoves;

  // Capture both original parents before lifting either view; the label may belong to the badge.
  const badgeParent = badge.getSuperview();
  const labelParent = label.getSuperview();
  if (!badgeParent || !labelParent) return;
  const root = getRootView();
  const position = badge.getPosition(root);
  const target = label.getPosition(root);
  const liftedViews = [
    { view: badge, parent: badgeParent },
    { view: label, parent: labelParent },
  ].map(({ view, parent }) => ({
    view,
    parent,
    nextSibling: parent.getSubviews()[parent.getSubviews().indexOf(view) + 1],
    position: parent.getPosition(root),
  }));

  // Center the shine on the icon rather than its top-left origin.
  const centerX =
    position.x +
    (position.width * Math.cos(position.r) -
      position.height * Math.sin(position.r)) *
      0.5;
  const centerY =
    position.y +
    (position.width * Math.sin(position.r) +
      position.height * Math.cos(position.r)) *
      0.5;

  // The topmost infinite container consumes input even while its dim is transparent.
  const container = new View({
    superview: root,
    width: root.style.width,
    height: root.style.height,
    infinite: true,
    canHandleEvents: true,
    zIndex:
      Math.max(100000, ...root.getSubviews().map((view) => view.style.zIndex)) +
      1,
  });
  const timeline = gsap.timeline({ paused: true });

  // Borrow views from one shared pool and track them for release after the animation.
  const pool = (sparklePool ??= new QuickViewPool(
    () =>
      new ImageScaleView({
        image: 'assets/ui/match2/movepinch/sparkle.png',
        canHandleEvents: false,
      }),
  ));
  const sparkles: ImageScaleView[] = [];

  // Cover the full viewport, including space outside the fixed design canvas.
  const screen = StateObserver.getState().ui.screenSize;
  const dim = new View({
    superview: container,
    x: screen.left,
    y: screen.top,
    width: screen.right - screen.left,
    height: screen.bottom - screen.top,
    backgroundColor: '#000000',
    opacity: 0,
  });

  // Keep the rotating shine above the dim but behind the lifted moves icon.
  const shine = new RadialShine({
    superview: container,
    width: 360,
    height: 360,
    x: centerX,
    y: centerY,
    scale: liftedViews[0].position.scale,
    centerOnOrigin: true,
    opacity: 0,
    canHandleEvents: false,
  });
  // Reproduce each original parent's transform so the views keep their local layout untouched.
  for (const { view, position } of liftedViews) {
    const holder = new View({
      superview: container,
      x: position.x,
      y: position.y,
      scale: position.scale,
      r: position.r,
    });
    holder.addSubview(view);
  }

  // Keep a stationary parent for the number and its independently fading sparkles.
  const particles = new View({
    superview: container,
    infinite: true,
    canHandleEvents: false,
  });

  // Aim the incoming bonus at the center of the existing count label.
  const bonus = new LangBitmapFontTextView({
    superview: particles,
    zIndex: 1,
    font: 'Title',
    color: '#FFFFFF',
    size: EXTRA_MOVES_FONT_SIZE,
    x: container.style.width * 0.5,
    y: Math.min(
      container.style.height * EXTRA_MOVES_SPAWN_Y_RATIO,
      centerY - EXTRA_MOVES_MIN_BADGE_GAP,
    ),
    centerOnOrigin: true,
    centerAnchor: true,
    align: 'center',
    verticalAlign: 'center',
    localeText: () => `+${bonusMoves}`,
    scale: 0.6,
    opacity: 0,
  });
  const counter = { moves: regularMoves };
  /** Last displayed integer, retained to avoid repeating the count sound between increments. */
  let displayedMoves = regularMoves;
  const targetX = target.x + target.width * 0.5;
  const targetY = target.y + target.height * 0.5;

  // Fade in the dim and shine together, then send the bonus into the label.
  timeline
    .to([dim.style, shine.style], { opacity: 0.5, duration: 0.25 })
    .addLabel('reveal')
    // Play the drop cue when the number and its particle burst appear.
    .call(
      () => {
        sounds.sfx.play('gachaItemDrop');
      },
      [],
      'reveal',
    )
    .to(bonus.style, {
      opacity: 1,
      scale: 1,
      duration: EXTRA_MOVES_SCALE_UP_DURATION,
      ease: 'back.out(1.5)',
    })
    .addLabel('flight', '+=0.35')
    .to(
      bonus.style,
      {
        x: targetX,
        y: targetY,
        scale: 0.55,
        duration: EXTRA_MOVES_TRAVEL_DURATION,
        ease: 'power2.in',
      },
      'flight',
    )
    // Celebrate the impact before the destination label starts counting up.
    .call(() => {
      sounds.sfx.play('win');
    })
    .to(bonus.style, { opacity: 0, duration: 0.12 })
    .to(
      counter,
      {
        moves: totalMoves,
        duration: 0.65,
        ease: 'none',
        onUpdate: () => {
          // Only update and play a tick when the displayed integer increases.
          const moves = Math.round(counter.moves);
          if (moves <= displayedMoves) return;
          displayedMoves = moves;
          setNumMoves(moves);
          sounds.sfx.play('progressCounted');
        },
      },
      '<',
    )
    .to([dim.style, shine.style], { opacity: 0, duration: 0.3 }, '+=0.2');

  // Burst in all directions as the number appears, leaving its center readable.
  for (let index = 0; index < 20; index++) {
    const angle = ((index + Math.random() * 0.5) / 20) * Math.PI * 2;
    const distance =
      BURST_PARTICLE_BASE_DISTANCE +
      Math.random() * BURST_PARTICLE_RANDOM_DISTANCE;
    sparkles.push(
      addSparkle({
        pool,
        timeline,
        bonus,
        particles,
        angle,
        radius: BURST_PARTICLE_SPAWN_RADIUS,
        driftX: Math.cos(angle) * distance,
        driftY: Math.sin(angle) * distance,
        size: 22 + Math.random() * 20,
        duration: BURST_PARTICLE_LIFETIME * (1 + Math.random() * 0.3),
        at: timeline.labels.reveal,
      }),
    );
  }

  // Emit only while the number is moving, from flight start through impact.
  const trailParticleCount = Math.ceil(EXTRA_MOVES_TRAVEL_DURATION * 60);
  for (let index = 0; index <= trailParticleCount; index++) {
    const progress = index / trailParticleCount;
    sparkles.push(
      addSparkle({
        pool,
        timeline,
        bonus,
        particles,
        angle: Math.random() * Math.PI * 2,
        radius:
          TRAIL_PARTICLE_MIN_RADIUS +
          Math.random() *
            (TRAIL_PARTICLE_MAX_RADIUS - TRAIL_PARTICLE_MIN_RADIUS),
        driftX: 0,
        driftY: 0,
        size: 12 + Math.random() * 16,
        duration: TRAIL_PARTICLE_LIFETIME,
        at: timeline.labels.flight + EXTRA_MOVES_TRAVEL_DURATION * progress,
      }),
    );
  }

  // Wait for the animation before restoring the popup and releasing input.
  await new Promise<void>((resolve) => {
    timeline.eventCallback('onComplete', resolve);
    timeline.play();
  });

  // Detach pooled views before returning them so they do not retain the temporary container.
  for (const sparkle of sparkles) {
    sparkle.removeFromSuperview();
    pool.releaseView(sparkle);
  }

  // Restore in reverse order so sibling references and nested labels are back before their badge.
  for (const { view, parent, nextSibling } of liftedViews.reverse()) {
    parent.addSubview(view, nextSibling);
  }
  setNumMoves(totalMoves);
  container.removeFromSuperview();
}

/**
 * Borrow and schedule a sparkle; zero drift keeps it at its captured spawn point.
 * @param options Pool, timeline, number and particle parent, plus emission and motion settings.
 * @returns Borrowed view that the caller must detach and return to the pool after the timeline stops.
 */
function addSparkle(options: {
  pool: QuickViewPool<ImageScaleView>;
  timeline: gsap.core.Timeline;
  bonus: LangBitmapFontTextView;
  particles: View;
  angle: number;
  radius: number;
  driftX: number;
  driftY: number;
  size: number;
  duration: number;
  at: number;
}): ImageScaleView {
  // Reset the transform and opacity left by the previous use before attaching the view.
  const sparkle = options.pool.obtainView();
  const size = options.size * 4;
  sparkle.updateOpts({
    superview: options.particles,
    width: size,
    height: size,
    centerOnOrigin: true,
    centerAnchor: true,
    x: 0,
    y: 0,
    scale: 1,
    r: 0,
    opacity: 0,
  });

  // Bitmap glyphs can extend outside the view box, so center on their rendered bounds.
  options.timeline.call(
    () => {
      const characters = options.bonus.characters;
      // Do not emit until the font has laid out the visible number.
      if (!characters?.length) return;

      const left = Math.min(...characters.map((character) => character.x));
      const right = Math.max(
        ...characters.map((character) => character.x + character.width),
      );
      const top = Math.min(...characters.map((character) => character.y));
      const bottom = Math.max(
        ...characters.map((character) => character.y + character.height),
      );
      const position = options.bonus.getPosition(options.particles);
      const centerX = position.x + (left + right) * 0.5 * position.scale;
      const centerY = position.y + (top + bottom) * 0.5 * position.scale;

      // Keep the emission radius steady even as the number shrinks during flight.
      sparkle.updateOpts({
        x: centerX + Math.cos(options.angle) * options.radius,
        y: centerY + Math.sin(options.angle) * options.radius,
        opacity: 1,
      });
    },
    [],
    options.at,
  );

  // Burst particles drift outward; trail particles stay at their captured spawn position.
  options.timeline.to(
    sparkle.style,
    {
      x: `+=${options.driftX}`,
      y: `+=${options.driftY}`,
      scale: 0,
      opacity: 0,
      duration: options.duration,
      ease: 'power2.out',
    },
    options.at,
  );

  // Keep ownership with this offer until its timeline has stopped referencing the view.
  return sparkle;
}
